import { useCallback, useState } from "react";

import type { CancelPolicyItemInput, InputMaybe } from "@/lib/gql/__generated__/graphql";

import { getOptionNumArray } from "@/components/ui/form/_utils";

import type { ForArrayType } from "../../_model/types/auto-generator-for-view-model";

type Props = {
	cancelPolicy: ForArrayType<InputMaybe<CancelPolicyItemInput[]> | undefined> | undefined;
	onSetViewModel: <K extends "cancelPolicy">(
		values: { key: K; value: ForArrayType<CancelPolicyItemInput[]> }[],
	) => void;
};

/** キャンセルポリシーで設定できる「〜日前」の上限日数 */
const MAX_BEFORE_DAYS = 90;

export const useCancelPolicy = ({ cancelPolicy, onSetViewModel }: Props) => {
	const [
		/** ベースのキャンセルポリシーに従うかどうか */
		useBaseCancelPolicy,
		setUseBaseCancelPolicy,
	] = useState<boolean>(cancelPolicy?.value?.length === 0);

	const toggleUseBaseCancelPolicy = useCallback(() => {
		setUseBaseCancelPolicy((prev) => {
			if (!prev) {
				onSetViewModel([{ key: "cancelPolicy", value: { isEdited: true, hasError: true, value: [] } }]);
			}

			return !prev;
		});
	}, [onSetViewModel]);

	const append = useCallback(() => {
		const lastItem = cancelPolicy?.value?.at(-1);

		if ((lastItem?.beforeDays?.value ?? 0) >= MAX_BEFORE_DAYS) {
			window.alert(`日付の${MAX_BEFORE_DAYS}日前以上の設定はできません`);
			return;
		}

		const newCancelPolicyItem = lastItem
			? {
					// すでにキャンセルポリシーが存在する場合
					chargeRatio: {
						isEdited: false,
						// 5%ずつ増加させる（100%が上限なので越えないようにする）
						value:
							(lastItem?.chargeRatio?.value ?? 0) + 5 >= 100
								? 100
								: (lastItem?.chargeRatio?.value ?? 0) + 5,
					},
					beforeDays: {
						isEdited: false,
						// 1日ずつ増加させる（上限日数を越えないようにする）
						value:
							(lastItem?.beforeDays?.value ?? 0) + 1 >= MAX_BEFORE_DAYS
								? MAX_BEFORE_DAYS
								: (lastItem?.beforeDays?.value ?? 0) + 1,
					},
				}
			: {
					// キャンセルポリシーがまだ存在しない場合
					chargeRatio: {
						isEdited: false,
						value: 0,
					},
					beforeDays: {
						isEdited: false,
						value: 0,
					},
				};

		const newCancelPolicy = {
			isEdited: true,
			hasError: true,
			value: cancelPolicy?.value ? [...cancelPolicy.value, newCancelPolicyItem] : [newCancelPolicyItem],
		};

		onSetViewModel([{ key: "cancelPolicy", value: newCancelPolicy }]);
	}, [cancelPolicy, onSetViewModel]);

	const removeCancelPolicyByIndex = useCallback(
		(index: number) => {
			const removedCancelPolicy = {
				isEdited: true,
				hasError: true,
				value: cancelPolicy?.value?.filter((_, i) => i !== index) ?? [],
			};

			onSetViewModel([{ key: "cancelPolicy", value: removedCancelPolicy }]);
		},
		[cancelPolicy, onSetViewModel],
	);

	const beforeDaysOptions = useCallback(
		(index: number) => {
			const step = 1;
			const min = 0;
			const max = MAX_BEFORE_DAYS;
			const maxLength = MAX_BEFORE_DAYS + 1;

			const options = getOptionNumArray(min, step, maxLength).filter((item) => {
				const targetDays = Number(item.value);

				const prevBeforeDays = cancelPolicy?.value?.[index - 1]?.beforeDays?.value;
				const nextBeforeDays = cancelPolicy?.value?.[index + 1]?.beforeDays?.value;

				if (!prevBeforeDays && !nextBeforeDays) {
					// 前後の日数がない場合は全て表示
					return true;
				}

				if (!prevBeforeDays && nextBeforeDays) {
					// 前の日数がなく、次の日数がある場合は、次の日数より小さいものを表示
					return targetDays < nextBeforeDays;
				}

				if (prevBeforeDays && !nextBeforeDays) {
					// 前の日数があり、次の日数がない場合は、前の日数より大きく、最大値以下のものを表示
					return prevBeforeDays < targetDays && targetDays <= max;
				}

				// 前後の日数がある場合は、前の日数より大きく、次の日数より小さいものを表示
				return prevBeforeDays! < targetDays && targetDays < nextBeforeDays!;
			});

			return options;
		},
		[cancelPolicy],
	);

	const updateBeforeDays = useCallback(
		(value: number, index: number) => {
			const newCancelPolicy = {
				isEdited: true,
				hasError: true,
				value: cancelPolicy!.value!.map((item, i) => {
					if (i === index) {
						return {
							...item,
							beforeDays: {
								isEdited: true,
								value,
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "cancelPolicy", value: newCancelPolicy }]);
		},
		[cancelPolicy, onSetViewModel],
	);

	const chargeRatioOptions = getOptionNumArray(0, 5, 21, { suffix: "％" });

	const updateChargeRatio = useCallback(
		(value: number, index: number) => {
			const newCancelPolicy = {
				isEdited: true,
				hasError: true,
				value: cancelPolicy!.value!.map((item, i) => {
					if (i === index) {
						return {
							...item,
							chargeRatio: {
								isEdited: true,
								value,
							},
						};
					}

					return item;
				}),
			};

			onSetViewModel([{ key: "cancelPolicy", value: newCancelPolicy }]);
		},
		[cancelPolicy, onSetViewModel],
	);

	return {
		cancelPolicy,

		useBaseCancelPolicy,
		toggleUseBaseCancelPolicy,

		append,
		removeCancelPolicyByIndex,

		beforeDaysOptions,
		updateBeforeDays,

		chargeRatioOptions,
		updateChargeRatio,
	};
};
